import { Button, FormControl, FormLabel, Stack } from '@chakra-ui/react'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useCallback, useMemo } from 'react'

import { InputGroup } from '@/components/InputGroup'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { formatDate } from '@/utils/formatDate'

const suggestDates = (args?: { count: number }) => {
  const { count } = args ?? { count: 5 }
  const today = new Date()

  return Array.from({ length: count }).map((_, index) => {
    const date = new Date(today)
    date.setDate(date.getDate() + index + 1)
    return date
  })
}

const suggestEndDates = (args: { count: number; date: Date }) => {
  const { count, date } = args

  return Array.from({ length: count }).map((_, index) => {
    const result = new Date(date)
    result.setDate(result.getDate() + index)
    return result
  })
}

export const InformationForm = () => {
  const methods = useFormContext<InsuranceParamsDto>()
  const { register, setValue, getValues, watch } = methods

  const isd = watch('generalParams.insuranceStartDate')
  const ied = watch('generalParams.insuranceEndDate')
  const { insuranceParams, isDisabled, isContracted } = useInsuranceParams()
  const origInsuranceEndDate = insuranceParams?.generalParams.insuranceEndDate

  const suggestedDates = suggestDates({ count: 5 })
  const minInsuranceEndDate = customAddDays(isd ?? new Date(), 1)
  const suggestedEndDates = useMemo(() => {
    return suggestEndDates({ count: 5, date: minInsuranceEndDate })
  }, [isd])

  const handleSuggestDateClick = useCallback(
    (date: Date) => {
      if (date)
        setValue('generalParams.insuranceStartDate', date)

      if (ied && date > new Date(ied))
        setValue('generalParams.insuranceEndDate', undefined)
    },
    [ied, setValue],
  )

  return (
    <>
      <FormBody>
        {isContracted && (
          <FormRow>
            <FormControl>
              <FormLabel>Číslo smlouvy</FormLabel>

              <InputGroup
                {...register('generalParams.contractNumber')}
                isDisabled
              />
            </FormControl>
          </FormRow>
        )}

        <FormRow
          sx={{
            gap: {
              base: 0,
              lg: 8,
            },
          }}
        >
          <FormControl>
            <FormLabel>Počátek pojištění</FormLabel>

            <Datepicker
              name="generalParams.insuranceStartDate"
              isDisabled={isDisabled}
            />
          </FormControl>

          {!isContracted && (
            <FormControl>
              <FormLabel
                sx={{
                  display: {
                    base: 'none',
                    lg: 'block',
                  },
                }}
              >
                &nbsp;
              </FormLabel>

              <Stack direction="row" spacing={2}>
                {suggestedDates.map((date) => (
                  <Button
                    key={date.toISOString()}
                    variant="ghost"
                    size="sm"
                    sx={{ h: 10 }}
                    onClick={() => handleSuggestDateClick(date)}
                    isDisabled={isDisabled}
                  >
                    {formatDate(date, 'dd D.M.')}
                  </Button>
                ))}
              </Stack>
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Konec pojištění</FormLabel>

            <Datepicker
              name="generalParams.insuranceEndDate"
              isDisabled={!!origInsuranceEndDate}
              options={{
                minDate: minInsuranceEndDate,
              }}
            />
          </FormControl>

          {!isContracted && (
            <FormControl>
              <FormLabel
                sx={{
                  display: {
                    base: 'none',
                    lg: 'block',
                  },
                }}
              >
                &nbsp;
              </FormLabel>

              <Stack direction="row" spacing={2}>
                {suggestedEndDates.map((date) => (
                  <Button
                    key={date.toISOString()}
                    variant="ghost"
                    size="sm"
                    sx={{ h: 10 }}
                    onClick={() =>
                      setValue('generalParams.insuranceEndDate', date)
                    }
                    isDisabled={isDisabled}
                  >
                    {formatDate(date, 'dd D.M.')}
                  </Button>
                ))}
              </Stack>
            </FormControl>
          )}
        </FormRow>
      </FormBody>
    </>
  )
}
